import type { ProvisionedMap, Ruleset, Seat } from '@aod/protocol';
import type { AiSlot, Alignment, ChatMessage, LobbyView, MatchInfo } from './types';

export interface DispatchCtx {
  get lobbyView(): LobbyView | null;
  setLobbyView(v: LobbyView | null): void;
  get matchInfo(): MatchInfo | null;
  setMatchInfo(v: MatchInfo | null): void;
  get chatHistory(): ChatMessage[];
  pushChat(msg: ChatMessage): void;
  closeGw(): void;
  /** This client's own lobby accountId (from lobby:created / lobby:joined). */
  selfId: string | undefined;
  /** Signed-in identity (Better Auth user id). Set for queued ranked players
   *  so match:found can derive localPlayerId without a lobby. */
  readonly localPlayerId?: string | undefined;
  emit(event: string, payload: unknown): void;
}

export function dispatchMessage(ctx: DispatchCtx, msg: Record<string, unknown>): void {
  switch (msg.type) {
    case 'lobby:created': {
      const created = msg as unknown as {
        lobbyId: string;
        inviteCode: string;
        player: { accountId: string; displayName: string };
      };
      ctx.selfId = created.player.accountId;
      ctx.setLobbyView({
        lobbyId: created.lobbyId,
        inviteCode: created.inviteCode,
        players: [
          {
            accountId: created.player.accountId,
            displayName: created.player.displayName,
            ready: true,
          },
        ],
        aiSlots: [],
        isHost: true,
        config: { maxPlayers: 4, mapSize: 'medium' },
        playerAlignments: {},
      });
      ctx.emit('lobby:updated', ctx.lobbyView);
      break;
    }
    case 'lobby:joined': {
      const joined = msg as unknown as {
        lobbyId: string;
        selfId?: string;
        players: { accountId: string; displayName: string }[];
        config: { maxPlayers: number; mapSize: string };
      };
      ctx.selfId = joined.selfId;
      ctx.setLobbyView({
        lobbyId: joined.lobbyId,
        inviteCode: '',
        players: joined.players.map((p) => ({ ...p, ready: false })),
        aiSlots: [],
        isHost: false,
        config: joined.config,
        playerAlignments: {},
      });
      ctx.emit('lobby:updated', ctx.lobbyView);
      break;
    }
    case 'lobby:player:joined': {
      if (ctx.lobbyView) {
        const joined = msg as unknown as { player: { accountId: string; displayName: string } };
        ctx.setLobbyView({
          ...ctx.lobbyView,
          players: [...ctx.lobbyView.players, { ...joined.player, ready: false }],
        });
      }
      ctx.emit('lobby:updated', msg);
      break;
    }
    case 'lobby:player:left': {
      if (ctx.lobbyView) {
        const left = msg as unknown as { playerId: string };
        ctx.setLobbyView({
          ...ctx.lobbyView,
          players: ctx.lobbyView.players.filter((p) => p.accountId !== left.playerId),
        });
      }
      ctx.emit('lobby:updated', msg);
      break;
    }
    case 'lobby:host:changed':
      ctx.emit('lobby:updated', msg);
      break;
    case 'lobby:config:updated':
      if (ctx.lobbyView) {
        const cfg = (
          msg as unknown as {
            config: { maxPlayers: number; mapSize: string; presetId?: string; teams?: boolean };
          }
        ).config;
        ctx.setLobbyView({ ...ctx.lobbyView, config: { ...ctx.lobbyView.config, ...cfg } });
      }
      ctx.emit('lobby:updated', msg);
      break;
    case 'lobby:ready:updated': {
      const readyMsg = msg as unknown as {
        readyStatus: Readonly<Record<string, boolean>>;
      };
      if (ctx.lobbyView) {
        ctx.setLobbyView({
          ...ctx.lobbyView,
          players: ctx.lobbyView.players.map((p) => ({
            ...p,
            ready: readyMsg.readyStatus[p.accountId] ?? false,
          })),
        });
      }
      ctx.emit('lobby:updated', msg);
      break;
    }
    case 'lobby:set-alignment': {
      const alignMsg = msg as unknown as { playerId: string; alignment: Alignment };
      if (ctx.lobbyView) {
        ctx.setLobbyView({
          ...ctx.lobbyView,
          playerAlignments: {
            ...ctx.lobbyView.playerAlignments,
            [alignMsg.playerId]: alignMsg.alignment,
          },
        });
      }
      if (ctx.matchInfo) {
        ctx.setMatchInfo({
          ...ctx.matchInfo,
          playerAlignments: {
            ...ctx.matchInfo.playerAlignments,
            [alignMsg.playerId]: alignMsg.alignment,
          },
        });
      }
      ctx.emit('lobby:updated', msg);
      break;
    }
    case 'lobby:ai-updated': {
      const aiMsg = msg as unknown as { aiSlots: readonly AiSlot[] };
      if (ctx.lobbyView) {
        ctx.setLobbyView({ ...ctx.lobbyView, aiSlots: aiMsg.aiSlots });
      }
      ctx.emit('lobby:updated', ctx.lobbyView);
      break;
    }
    case 'match:found': {
      const found = msg as unknown as {
        matchId: string;
        serverUrl: string;
        seed: string;
        players: { accountId: string; displayName: string }[];
        aiSlots?: readonly AiSlot[];
        totalPlayers?: number;
        /** The gateway's stamp of the recipient's own accountId (queue path). */
        selfId?: string;
        map?: ProvisionedMap;
        ruleset?: Ruleset;
        seats?: readonly Seat[];
        teams?: readonly number[];
      };
      // Derive local player ID. The gateway's own stamp wins: it is the only
      // authority on which of the matched players this socket is, and it is
      // the only one that can name an anonymous queued player (whose id is a
      // gateway-internal uuid the client never sees). Then the signed-in
      // identity — the queue path for a ranked player, whose id IS the
      // accountId — and then the lobby-derived choice. The old fallback
      // grabbed players[0], assigning a queued player someone else's seat.
      const stamped = found.selfId;
      const stampedInMatch =
        stamped !== undefined && found.players.some((p) => p.accountId === stamped);
      const identity = ctx.localPlayerId;
      const identityInMatch =
        identity !== undefined && found.players.some((p) => p.accountId === identity);
      const localPlayerId = stampedInMatch
        ? (stamped as string)
        : identityInMatch
          ? (identity as string)
          : ctx.selfId !== undefined && found.players.some((p) => p.accountId === ctx.selfId)
            ? ctx.selfId
            : (ctx.lobbyView?.players[0]?.accountId ?? found.players[0]?.accountId ?? '');
      ctx.setMatchInfo({
        matchId: found.matchId,
        seed: found.seed,
        players: found.players,
        aiSlots: found.aiSlots ?? [],
        localPlayerId,
        serverUrl: found.serverUrl,
        totalPlayers: found.totalPlayers ?? found.players.length,
        playerAlignments: {},
        // The resolved map — the seat builds the same board the server did.
        ...(found.map ? { map: found.map } : {}),
        // A preset's ruleset: every seat plays the game the server plays.
        ...(found.ruleset ? { ruleset: found.ruleset } : {}),
        ...(found.seats ? { seats: found.seats } : {}),
        ...(found.teams ? { teams: found.teams } : {}),
      });
      ctx.emit('match:found', ctx.matchInfo);
      ctx.closeGw();
      break;
    }
    case 'queue:status':
      ctx.emit('queue:status', msg);
      break;
    case 'queue:error':
      ctx.emit('queue:error', msg);
      break;
    case 'match:starting':
      ctx.emit('match:starting', msg);
      break;
    case 'input:frame':
      ctx.emit('input:frame', msg);
      break;
    case 'player:disconnect':
      ctx.emit('player:disconnect', msg);
      break;
    case 'player:reconnect':
      ctx.emit('player:reconnect', msg);
      break;
    case 'player:timeout':
      ctx.emit('player:timeout', msg);
      break;
    case 'rejoin:ack':
      ctx.emit('rejoin', msg);
      break;
    case 'match:end':
      ctx.emit('match:end', msg);
      break;
    case 'match:confirm_forfeit':
      ctx.emit('match:confirm_forfeit', msg);
      break;
    case 'chat:say':
      ctx.pushChat({
        sender: msg.sender as string,
        text: msg.text as string,
        timestamp: Date.now(),
      });
      ctx.emit('chat', msg);
      break;
  }
}
